import { Link } from '@material-ui/core';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  ConfirmationToken as StripeConfirmationToken,
  StripeError,
  StripePaymentElementChangeEvent,
} from '@stripe/stripe-js';
import { Logger } from 'aws-amplify';
import clsx from 'clsx';
import { Body, Button, Heading } from 'copilot-design-system';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import Stripe from 'stripe';

import { PortalConfigContext } from 'src/context';
import { usePaymentElementStyles } from 'src/features/billing/StripePaymentElement/hooks/useStripeElementStyles';
import {
  hasValidateEmail,
  hasValidateName,
  UnAuthCheckoutForm,
} from 'src/features/billing/StripePaymentElement/UnAuthCheckoutForm';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { StripeCurrencyFormatterAdapter } from 'src/legacy/components/Formatters/Formatters';
import {
  PaymentOptions,
  PaymentSourceType,
} from 'src/services/api/invoicesApi';
import { useGetCustomerSessionQuery } from 'src/services/api/usersApi';
import { alertSnackbar } from 'src/store/ui/actions';
import { StripeElementsLayout, StripePaymentMethod } from 'src/types/payment';

export type SignUpData = {
  name: string;
  email: string;
  company: string;
};

type StripePaymentElementProps = {
  id: string;
  onSubmitCallback: (
    confirmationToken?: StripeConfirmationToken,
    signUpData?: SignUpData,
  ) => void;
  selectedSource: StripePaymentMethod | string | null;
  setSelectedSource: (
    paymentSource: Stripe.CustomerSource | string | null,
  ) => void;
  amount: number;
  currency: string;
  paymentOptions: PaymentOptions;
  onBehalfOf?: string;
  isPaying: boolean;
  setIsPaying: (isPaying: boolean) => void;
  isSubscriptionInvoice: boolean;
  isAutoChargeSubscription: boolean;
  isActualCheckout: boolean;
  layout: StripeElementsLayout;
  isUnAuthPayer: boolean;
  customerSessionTarget: string;
};

const logger = new Logger('StripePaymentElement');

export const StripePaymentElement = ({
  id,
  onSubmitCallback,
  selectedSource,
  setSelectedSource,
  amount,
  currency,
  paymentOptions,
  onBehalfOf,
  isPaying,
  setIsPaying,
  isSubscriptionInvoice,
  isAutoChargeSubscription,
  isActualCheckout,
  layout,
  isUnAuthPayer,
  customerSessionTarget,
}: StripePaymentElementProps) => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const activeCompanyId = useAppSelector(
    (state) => state.activeCompany.activeCompanyId,
  );
  const { name: portalName, features } = React.useContext(PortalConfigContext);
  const [isOptionsUpdated, setIsOptionsUpdated] = useState<boolean>(false);
  const [unAuthPayer, setUnAuthPayer] = useState({
    name: '',
    email: '',
    company: '',
  });

  const styles = usePaymentElementStyles();
  const { data: customerSession, isFetching: isFetchingCustomerSession } =
    useGetCustomerSessionQuery({
      target: customerSessionTarget,
      companyId: activeCompanyId,
    });
  const [isPaymentInformationFilled, setIsPaymentInformationFilled] =
    React.useState(false);

  useEffect(() => {
    // Note: Stripe Elements throws a fit if amount and currency are not valid.
    // It is important that we check for these conditions before updating it.
    if (
      elements &&
      currency &&
      currency.length &&
      (isUnAuthPayer || customerSession || !isActualCheckout) &&
      styles
    ) {
      const paymentMethodTypes = [];
      if (paymentOptions.allowPaymentViaACH) {
        paymentMethodTypes.push('us_bank_account');
      }
      if (paymentOptions.allowPaymentViaCC) {
        paymentMethodTypes.push('card');
      }

      elements.update({
        currency: currency.toLowerCase(),
        paymentMethodTypes,
        amount: amount && amount > 0 ? amount : undefined,
        ...styles,
        customerSessionClientSecret: customerSession,
        setupFutureUsage:
          isSubscriptionInvoice || isUnAuthPayer ? 'off_session' : null,
      });

      setIsOptionsUpdated(true);
    }
  }, [
    elements,
    styles,
    paymentOptions,
    isSubscriptionInvoice,
    id,
    currency,
    amount,
    isFetchingCustomerSession,
    customerSession,
    isUnAuthPayer,
    isActualCheckout,
  ]);

  const showSnackbarError = (error: string) => {
    dispatch(
      alertSnackbar({
        errorMessage: error,
      }),
    );
  };

  const handleError = (error: StripeError) => {
    logger.error(error);
    showSnackbarError(error.message || 'An error occurred');
    setIsPaying(false);
  };

  const handlePaymentElementChange = (
    event: StripePaymentElementChangeEvent,
  ) => {
    if (event.value.type) {
      setSelectedSource(event.value.type);
    }
    setIsPaymentInformationFilled(Boolean(event.complete));
  };

  const handleSubmit: MouseEventHandler<HTMLButtonElement> = (event) => {
    const eventFn = async () => {
      event.preventDefault();

      if (!stripe || !elements) return;

      const { error: submitError } = await elements.submit();
      if (submitError) {
        handleError(submitError);
        return;
      }
      const { error, confirmationToken } = await stripe.createConfirmationToken(
        {
          elements,
        },
      );

      if (error) {
        handleError(error);
        return;
      }

      let signUpData: SignUpData | undefined;
      if (isUnAuthPayer) {
        const { name, email, company } = unAuthPayer;
        if (selectedSource === 'card') {
          signUpData = {
            name,
            email,
            company,
          };
        } else {
          signUpData = {
            name:
              confirmationToken.payment_method_preview.billing_details.name ??
              name,
            email:
              confirmationToken.payment_method_preview.billing_details.email ??
              email,
            company,
          };
        }
      }

      onSubmitCallback(confirmationToken, signUpData);
    };
    eventFn().catch(() => {});
  };

  return stripe &&
    elements &&
    ((onBehalfOf && onBehalfOf !== '') || !isActualCheckout) &&
    isOptionsUpdated ? (
    <div
      className={clsx(' box-border relative overflow-auto', {
        'px-6 lg:px-3 xl:px-6 w-full lg:w-[477px] py-6 lg:py-4 ':
          isActualCheckout,
        'px-4 py-3 w-full': !isActualCheckout,
      })}
    >
      <div
        className={clsx({
          'pb-6': isActualCheckout,
          'pb-3': !isActualCheckout,
        })}
      >
        {/** This options also accounts for checking if currency is USD or not */}
        {paymentOptions.allowPaymentViaACH && (
          <Heading className="text-[16px] font-medium text-gray-small mb-2">
            Payment Method
          </Heading>
        )}
        <div className="border lg:border-0 border-non-hover-border border-solid rounded-lg">
          <PaymentElement
            options={{
              layout: layout,

              business: {
                name: portalName,
              },
              readOnly: isPaying,
              // preview doesn't need to show options beyond card and bank
              ...(!isActualCheckout && {
                wallets: {
                  link: 'never',
                },
              }),
            }}
            onChange={handlePaymentElementChange}
          />
          {!isUnAuthPayer &&
            selectedSource === 'us_bank_account' &&
            !isPaymentInformationFilled && (
              <div className="text-[14px] text-gray-small mt-2">
                <Link
                  className="inline-block text-black-heading hover:text-gray-small font-bold"
                  href="/settings/billing"
                >
                  Enter bank details manually instead
                </Link>{' '}
                (takes 1-2 business days)
              </div>
            )}
        </div>
      </div>
      {isUnAuthPayer && (
        <UnAuthCheckoutForm
          onFormChanged={setUnAuthPayer}
          companiesDisabled={!!features.disableCompanies}
          initialData={unAuthPayer}
          selectedSource={selectedSource}
        />
      )}
      <div>
        <Button
          className="w-full h-12"
          variant="primary"
          label={
            isAutoChargeSubscription
              ? 'Subscribe'
              : `Pay ${StripeCurrencyFormatterAdapter({
                  value: amount,
                  currency,
                })}`
          }
          onClick={handleSubmit}
          disabled={
            !isPaymentInformationFilled ||
            (isUnAuthPayer &&
              selectedSource === PaymentSourceType.Card &&
              (!hasValidateName(unAuthPayer.name) ||
                !hasValidateEmail(unAuthPayer.email)))
          }
          loading={isPaying}
        />
      </div>
      {isAutoChargeSubscription && (
        <div
          className={clsx({
            'mt-4': isActualCheckout,
            'mt-2': !isActualCheckout,
          })}
        >
          <Body className="text-gray-small text-[13px] font-normal leading-[21px] text-center">
            {`By confirming your subscription, you allow ${portalName} to charge you for future payments in accordance with their terms.`}
          </Body>
        </div>
      )}
    </div>
  ) : null;
};
